import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Hand, CheckCircle2, ListPlus, Send } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../TimerSummaryModal';
import HelpTip from '../ui/HelpTip';
import { useStartTimer } from '../home/useHomeData';
import { undoableTicketStatus } from '../../utils/undoableTicketStatus';
import type { ActiveTimer } from '../../types';
import { pausedNotice, statusAfterDone, type DoneMark, type ReplyNotice } from './replyTimerModel';

/** h:mm:ss since `start`, ticking every second. */
export function ElapsedClock({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const secs = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return <bdi dir="ltr" className="tabular-nums">{`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}</bdi>;
}

/**
 * P-37 (Q-180 / Q-182) — the small line above the composer that fades by itself:
 *   "המענה נשלח · נרשמו N דק' · [עדיין עובדת על זה]"
 *   "המשימה שרצה הושהתה אוטומטית ותחזור לרוץ אחרי 'גמרתי' · [שניהם במקביל]"
 */
export function ReplyNoticeLine({ notice, onStillOnIt, onRunBoth, onClose }: {
  notice: ReplyNotice;
  onStillOnIt: () => void;
  onRunBoth: (replyTimerId: string) => void;
  /** D-252 — manual close (the paused-task line stays until then, or until "גמרתי"). */
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const link = 'font-medium text-accent-text hover:underline underline-offset-2';
  return (
    <p role="status" className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-xs text-text-2" data-testid="reply-notice">
      {notice.kind === 'unlogged' ? (
        <span>{t('replyTimer.unloggedLine')}</span>
      ) : notice.kind === 'sent' ? (
        <>
          <span>{t('replyTimer.sentLine', { n: notice.minutes })}</span>
          <span aria-hidden="true">·</span>
          <button type="button" className={link} onClick={onStillOnIt}>{t('replyTimer.stillOnIt')}</button>
        </>
      ) : (
        <>
          <span>{t('replyTimer.pausedLine', { title: notice.titles.join(', ') })}</span>
          <span aria-hidden="true">·</span>
          <button type="button" className={link} onClick={() => onRunBoth(notice.replyTimerId)}>{t('replyTimer.runBoth')}</button>
          {onClose && (
            <button type="button" className="ms-auto text-text-2 hover:text-text" onClick={onClose} aria-label={t('shell.close')} data-testid="reply-notice-close">×</button>
          )}
        </>
      )}
    </p>
  );
}

/**
 * P-37 (D-194 Q-172 / Q-173 / Q-182) — next to the composer of a SERVICE
 * TICKET, prominent: "אני על זה" (a reply timer on the ticket itself; no
 * task) and "יצירת משימה מהקריאה". While it runs: the time, "גמרתי" (no
 * window, no "מה בוצע"; a "מענה לקריאה" log), and the optional mark
 * "הקריאה הסתיימה" / "מחכה ללקוח" (none: the ticket stays "בטיפול").
 * The (?) explains the three ways (the D-194 texts, on hover).
 */
export default function ReplyActionsBar({
  ticketId,
  title,
  replyTimer,
  onCreateTask,
  onForward,
  onNotice,
}: {
  ticketId: string;
  title: string;
  /** The caller's running "אני על זה" on this ticket, or null. */
  replyTimer: ActiveTimer | null;
  onCreateTask?: () => void;
  /** D-317 §6 — "העברה": hand the ticket to another employee (`ForwardTicketDialog`). */
  onForward?: () => void;
  onNotice: (n: ReplyNotice | null) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [mark, setMark] = useState<DoneMark>('none');

  const start = useStartTimer({
    onStarted: (_task, info) => {
      onNotice(pausedNotice(info?.timerId, info?.paused));
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
    },
  });

  const done = useMutation({
    mutationFn: () => api.finishReplyTimer(ticketId),
    onSuccess: (res) => {
      const d = res?.data;
      if (d?.logged) toast('success', t('replyTimer.doneLogged', { n: Math.round(Number(d.duration_minutes) || 0) }));
      else toast('info', t('replyTimer.doneUnderMinute'));
      if (d?.resumed && d.resumed.length > 0) toast('info', t('replyTimer.resumedAfterDone', { title: d.resumed.map((r) => r.ticket_title || '').join(', ') }));
      const next = statusAfterDone(mark);
      if (next) undoableTicketStatus({ qc, id: ticketId, status: next, taskType: 'ticket' });
      setMark('none');
      onNotice(null);
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('replyTimer.doneFailed')),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['timer-status'] });
      qc.invalidateQueries({ queryKey: ['work-logs'] });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });

  const help = (
    <HelpTip
      openOnHover
      title={t('replyTimer.help.title')}
      body={[t('replyTimer.help.box'), t('replyTimer.help.imOnIt'), t('replyTimer.help.createTask')].map((x) => `• ${x}`).join('\n')}
    />
  );

  const createTask = onCreateTask ? (
    <button type="button" onClick={onCreateTask} className="btn btn-secondary btn-sm">
      <ListPlus size={16} aria-hidden="true" /> {t('replyTimer.createTask')}
    </button>
  ) : null;

  const forward = onForward ? (
    <button type="button" onClick={onForward} className="btn btn-secondary btn-sm" data-testid="reply-forward">
      <Send size={16} className="rtl-mirror" aria-hidden="true" /> {t('replyTimer.forward')}
    </button>
  ) : null;

  if (!replyTimer) {
    return (
      <div className="mb-2 flex flex-wrap items-center gap-2 px-1" data-testid="reply-actions">
        <button
          type="button"
          onClick={() => start.mutate({ id: ticketId, title, reply: true })}
          disabled={start.isPending}
          aria-busy={start.isPending || undefined}
          className="btn btn-primary btn-sm"
        >
          <Hand size={16} aria-hidden="true" /> {t('replyTimer.imOnIt')}
        </button>
        {forward}
        {createTask}
        {help}
      </div>
    );
  }

  const marks: Array<Exclude<DoneMark, 'none'>> = ['completed', 'waiting_customer'];
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-accent-soft px-3 py-2" data-testid="reply-actions">
      <span className="flex items-center gap-1.5 text-sm font-medium text-text">
        <Hand size={16} aria-hidden="true" />
        {t('replyTimer.running')} · <ElapsedClock start={replyTimer.started_at} />
      </span>
      <button
        type="button"
        onClick={() => done.mutate()}
        disabled={done.isPending}
        aria-busy={done.isPending || undefined}
        className="btn btn-primary btn-sm"
      >
        <CheckCircle2 size={16} aria-hidden="true" /> {t('replyTimer.done')}
      </button>
      <div role="radiogroup" aria-label={t('replyTimer.markLabel')} className="flex flex-wrap items-center gap-1.5">
        {marks.map((m) => {
          const active = mark === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setMark(active ? 'none' : m)}
              className={`rounded-full border px-3 py-1 text-xs font-medium min-h-8 transition-colors ${
                active ? 'border-accent bg-surface-0 text-accent-text' : 'border-border bg-surface-0 text-text-2 hover:bg-surface-subtle'
              }`}
            >
              {t(`replyTimer.mark.${m}`)}
            </button>
          );
        })}
      </div>
      <span className="ms-auto flex items-center gap-2">
        {forward}
        {createTask}
        {help}
      </span>
    </div>
  );
}
